import { useCallback, useEffect } from 'react';
import { Flex } from '@servicetitan/anvil2';
import { ErrorBoundary, LoadingScreen } from '@servicetitan/financing-utils-shared';
import {
    FinancingInformationContainer,
    PlansContainer,
    AutoProviderLogo,
    FinancedAmountStore,
} from '@servicetitan/financing-plans-shared';
import { DecisionDataStore } from '@servicetitan/financing-decision-shared';
import { WorkflowStore, SetupDataStore } from '@servicetitan/financing-workflow-shared';
import { Calculation, SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import { useRecalculateOffer } from './hooks/useRecalculateOffer';
import { plansPanel, wrapper } from './ApplicationWorkflow.module.scss';

export const UpdateOfferWorkflow = () => {
    const { financedAmount, setFinancedAmount } = FinancedAmountStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { setDecisionData, decisionData } = DecisionDataStore.useStore();
    const { setupData } = SetupDataStore.useStore();

    useEffect(() => {
        if (decisionData?.approvedAmount) {
            setFinancedAmount({ saleAmount: decisionData.approvedAmount, downPayment: 0 });
        }
    }, [decisionData?.approvedAmount, setFinancedAmount]);

    const { recalculateOffer, isSubmitting } = useRecalculateOffer({
        onSuccess: updatedLoan => {
            // Update decision data with the new loan details
            setDecisionData({
                ...decisionData,
                loanId: updatedLoan.id ?? null,
                externalLoanId: updatedLoan.externalLoanId ?? null,
                decision: SubmitApplicationDecision.Approved,
                approvedAmount: updatedLoan.approvedAmount ?? null,
                term: updatedLoan.plan?.term ?? null,
                rate: updatedLoan.plan?.apr ? parseFloat(updatedLoan.plan.apr) : null,
                payment: updatedLoan.payment ?? null,
                description: updatedLoan.plan?.description ?? null,
                planNumber: updatedLoan.plan?.planNumber ?? null,
            });

            // Navigate to the accept-offer stage
            setWorkflowStage('accept-offer');
        },
    });

    const handlePlanSelected = useCallback(
        (plan: Calculation) => {
            if (!setupData?.loanId || !financedAmount) {
                return;
            }

            recalculateOffer(setupData.loanId, {
                planNumber: plan.planNumber ?? null,
                loanAmount: financedAmount,
            });
        },
        [setupData?.loanId, financedAmount, recalculateOffer],
    );

    if (isSubmitting) {
        return <LoadingScreen message="Updating your offer..." />;
    }

    // Use approved amount as maximum for update offer flow
    const maxAmount = decisionData?.approvedAmount ?? undefined;

    return (
        <div className={plansPanel} style={{ width: '100%' }}>
            <div className={wrapper}>
                <Flex direction="column" gap="4">
                    <ErrorBoundary actionCausingError="loading available plans">
                        <AutoProviderLogo />
                        <FinancingInformationContainer maxAmount={maxAmount} />
                        <PlansContainer onPlanSelected={handlePlanSelected} />
                    </ErrorBoundary>
                </Flex>
            </div>
        </div>
    );
};
